import React, { useState } from "react";
import "./styles.scss";
import Tooltip from "../Tooltip";

const GrowthProgress = ({ filteredGrowthData }) => {
  const [expandedSections, setExpandedSections] = useState({
    basic: false,
    inProgress: false,
    mastered: false,
  });
  const [currentPage, setCurrentPage] = useState({
    basic: 1,
    inProgress: 1,
    mastered: 1,
  });
  const itemsPerPage = 12;

  const growthProgressTooltip = (
    <>
      <h4>Understanding Your Progress</h4>
      <p>Your progress is divided into three levels:</p>
      <ul>
        <li>
          <strong>Rookie (0-60%)</strong>: Start your journey by completing
          practice sets and tests to build fundamental understanding.
        </li>
        <li>
          <strong>Challenger (60-90%)</strong>: You're close to mastery! Focus
          on completing all practice sets and tests to reach champion level.
        </li>
        <li>
          <strong>Champion Level (90-100%)</strong>: You've mastered these
          topics! Maintain your skills with occasional practice.
        </li>
      </ul>
      <p>
        <strong>How to Progress:</strong>
      </p>
      <ol>
        <li>Complete all practice sets in each topic</li>
        <li>Attempt all available tests</li>
        <li>Review incorrect answers to improve</li>
        <li>Focus on topics where you're close to the next level</li>
      </ol>
    </>
  );

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
    setCurrentPage((prev) => ({
      ...prev,
      [section]: 1,
    }));
  };

  const getPaginatedTopics = (topics, section) => {
    const startIndex = (currentPage[section] - 1) * itemsPerPage;
    return topics.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalPages = (count) => Math.ceil(count / itemsPerPage);

  return (
    <div className="diagnostic-section growth-progress">
      <div className="section-header">
        <h2>Growth Progress</h2>
        <p>Track your learning journey and improvement over time</p>
        <Tooltip content={growthProgressTooltip} />
      </div>

      <div className="growth-progress-content">
        <div className="pyramid-chart">
          <svg width="400" height="500" viewBox="0 0 400 500">
            {/* Background pyramid shape */}
            <path
              d="M200 50 L350 450 L50 450 Z"
              fill="#f7fafc"
              stroke="#e2e8f0"
              strokeWidth="2"
            />

            {/* Basic section (bottom third) */}
            <path
              d="M200 450 L50 450 L350 450 L200 350 Z"
              fill="#EF5350"
              opacity="0.1"
            />
            <text
              x="200"
              y="420"
              textAnchor="middle"
              fill="#EF5350"
              fontSize="24"
              fontWeight="600"
            >
              {filteredGrowthData?.basic?.count || 0}
            </text>
            <text
              x="200"
              y="445"
              textAnchor="middle"
              fill="#EF5350"
              fontSize="14"
              fontWeight="500"
            >
              Rookie
            </text>

            {/* In Progress section (middle third) */}
            <path
              d="M200 350 L50 450 L350 450 L200 250 Z"
              fill="#FFA726"
              opacity="0.1"
            />
            <text
              x="200"
              y="320"
              textAnchor="middle"
              fill="#FFA726"
              fontSize="24"
              fontWeight="600"
            >
              {filteredGrowthData?.inProgress?.count || 0}
            </text>
            <text
              x="200"
              y="345"
              textAnchor="middle"
              fill="#FFA726"
              fontSize="14"
              fontWeight="500"
            >
              Challenger
            </text>

            {/* Mastered section (top third) */}
            <path
              d="M200 250 L50 450 L350 450 L200 50 Z"
              fill="#4CAF50"
              opacity="0.1"
            />
            <text
              x="200"
              y="220"
              textAnchor="middle"
              fill="#4CAF50"
              fontSize="24"
              fontWeight="600"
            >
              {filteredGrowthData?.mastered?.count || 0}
            </text>
            <text
              x="200"
              y="245"
              textAnchor="middle"
              fill="#4CAF50"
              fontSize="14"
              fontWeight="500"
            >
              Champion
            </text>
          </svg>
        </div>

        <div className="topics-details">
          {Object.entries(filteredGrowthData).map(([key, data]) => (
            <div key={key} className={`topics-section ${key}`}>
              <div
                className="section-header"
                onClick={() => toggleSection(key)}
              >
                <h3>
                  {key === "basic"
                    ? "Rookie Topics"
                    : key === "inProgress"
                    ? "Challenger Topics"
                    : "Champion Topics"}{" "}
                  ({data.count})
                </h3>
                <span
                  className={`toggle-icon ${
                    expandedSections[key] ? "expanded" : ""
                  }`}
                >
                  ▼
                </span>
              </div>

              {expandedSections[key] && (
                <>
                  {data.topics.length === 0 ? (
                    <div
                      className="no-growth-message"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "32px 0",
                        color:
                          key === "basic"
                            ? "#EF5350"
                            : key === "inProgress"
                            ? "#FFA726"
                            : "#4CAF50",
                        background: "#f8f9fa",
                        borderRadius: "12px",
                        minHeight: "120px",
                        fontSize: "1.1rem",
                        fontWeight: 500,
                      }}
                    >
                      <svg
                        width="60"
                        height="60"
                        viewBox="0 0 80 80"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="40"
                          cy="40"
                          r="40"
                          fill={
                            key === "basic"
                              ? "#FFEBEE"
                              : key === "inProgress"
                              ? "#FFF3E0"
                              : "#E8F5E9"
                          }
                        />
                        <path
                          d="M40 20L50 40H30L40 20Z"
                          fill={
                            key === "basic"
                              ? "#EF5350"
                              : key === "inProgress"
                              ? "#FFA726"
                              : "#4CAF50"
                          }
                        />
                        <rect
                          x="35"
                          y="40"
                          width="10"
                          height="20"
                          rx="5"
                          fill={
                            key === "basic"
                              ? "#EF5350"
                              : key === "inProgress"
                              ? "#FFA726"
                              : "#4CAF50"
                          }
                        />
                        <circle
                          cx="40"
                          cy="65"
                          r="5"
                          fill={
                            key === "basic"
                              ? "#EF5350"
                              : key === "inProgress"
                              ? "#FFA726"
                              : "#4CAF50"
                          }
                        />
                      </svg>
                      <div style={{ marginTop: 16, textAlign: "center" }}>
                        <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>
                          {key === "basic"
                            ? "No Rookie Topics Yet"
                            : key === "inProgress"
                            ? "No Challenger Topics Yet"
                            : "No Champion Topics Yet"}
                        </div>
                        <div
                          style={{
                            marginTop: 6,
                            color: "#555",
                            fontWeight: 400,
                          }}
                        >
                          {key === "basic"
                            ? "Once you start practicing, your rookie topics will appear here."
                            : key === "inProgress"
                            ? "Topics close to mastery will show up here as you progress."
                            : "Achieve 90%+ in a topic and complete all resources to become a Champion!"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="topics-list">
                        {getPaginatedTopics(data.topics, key).map(
                          (topic, index) => (
                            <div key={index} className="topic-item">
                              <span className="topic-name">{topic.name}</span>
                              <span className={`status-badge ${key}`}>
                                {key === "basic"
                                  ? "Rookie"
                                  : key === "inProgress"
                                  ? "Challenger"
                                  : "Champion"}
                              </span>
                            </div>
                          )
                        )}
                      </div>

                      {data.topics.length > itemsPerPage && (
                        <div className="pagination-controls">
                          <button
                            onClick={() =>
                              setCurrentPage((prev) => ({
                                ...prev,
                                [key]: Math.max(1, prev[key] - 1),
                              }))
                            }
                            disabled={currentPage[key] === 1}
                          >
                            Previous
                          </button>
                          <span className="page-info">
                            Page {currentPage[key]} of{" "}
                            {totalPages(data.topics.length)}
                          </span>
                          <button
                            onClick={() =>
                              setCurrentPage((prev) => ({
                                ...prev,
                                [key]: Math.min(
                                  totalPages(data.topics.length),
                                  prev[key] + 1
                                ),
                              }))
                            }
                            disabled={
                              currentPage[key] ===
                              totalPages(data.topics.length)
                            }
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GrowthProgress;
